import React, { useCallback, useMemo } from 'react';
import { AccommodationsConsoleWithHealth } from '../../AccommodationsConsole';
import { HotelSearchMapLayout } from '@shared/constants/accommodations';
import type { ScenarioConsoleProps, ScenarioConfig } from '../scenarios.types';
import type { TopBarConfig, Filters } from '@shared/types/Accommodations.types';
import { CashFundListingEntry } from '../components/listing-entry/CashFundListingEntry';
import type { ScenarioListingEntryProps } from '../components/listing-entry/ListingEntry.types';
import { CashFundBanner } from './CashFundBanner';
import { CashFundFilterToggle } from './CashFundFilterToggle';
import { CASH_FUND_PAGE_BACKGROUND, CASH_FUND_GOLD, CASH_FUND_STATIC_AVAILABLE_BALANCE } from './cashFund.constants';

type CashFundConsoleProps = Readonly<
  ScenarioConsoleProps & {
    layout?: HotelSearchMapLayout;
  }
>;

const CASH_FUND_TOP_BAR: TopBarConfig = {
  searchForm: { dateRangeEnabled: true, occupancySelectorEnabled: true },
  showJoyLogo: true,
  showCloseButton: false,
  backgroundColor: CASH_FUND_PAGE_BACKGROUND,
};

export const CashFundAccommodationsConsole = ({ eventHandle, eventName, onClose, selectedHotelId, layout = HotelSearchMapLayout.FULL }: CashFundConsoleProps) => {
  const scenarioConfig = useMemo<ScenarioConfig>(
    () => ({
      variant: 'cashFund',
      topBar: CASH_FUND_TOP_BAR,
      mapLayoutMode: layout,
      pageBackgroundColor: CASH_FUND_PAGE_BACKGROUND,
      markerColor: CASH_FUND_GOLD,
      accentColor: CASH_FUND_GOLD,
      markerOutOfPocketBalance: CASH_FUND_STATIC_AVAILABLE_BALANCE,
    }),
    [layout],
  );

  const renderListingEntry = useCallback((props: ScenarioListingEntryProps) => <CashFundListingEntry {...props} />, []);
  const renderListingsHeader = useCallback(() => <CashFundBanner />, []);
  const renderFiltersHeader = useCallback(
    ({ filters, onFiltersChange }: { filters: Filters; onFiltersChange: (filters: Filters) => void }) => (
      <CashFundFilterToggle
        checked={!!filters.cashFundFullyCoveredOnly}
        onChange={checked => onFiltersChange({ ...filters, cashFundFullyCoveredOnly: checked })}
      />
    ),
    [],
  );

  return (
    <AccommodationsConsoleWithHealth
      eventHandle={eventHandle}
      eventName={eventName}
      onClose={onClose}
      selectedHotelId={selectedHotelId}
      scenarioConfig={scenarioConfig}
      renderListingEntry={renderListingEntry}
      renderListingsHeader={renderListingsHeader}
      renderFiltersHeader={renderFiltersHeader}
    />
  );
};
